import { useEffect, useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { api, formatApiErrorDetail } from "@/lib/api";
import { exportImageHD } from "@/lib/exportUtils";
import { SectionCard } from "./SectionCard";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Upload, Loader2, Sheet, Image as ImageIcon, Check, ChevronsUpDown, X, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const money = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nfmt = (n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 3 });

export function ImportBtn({ onDone, endpoint = "/cuentas-detalle/import", label = "Cargar PDF detallado", testid = "cuentas-detalle", vendedores = [], requireVendedor = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [vendedor, setVendedor] = useState("");
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (requireVendedor && !vendedor.trim()) {
      toast.error("Seleccioná el vendedor antes de subir el PDF");
      if (ref.current) ref.current.value = "";
      return;
    }
    setBusy(true);
    if (ref.current) ref.current.value = "";
    const finish = (ok, msg, diag) => {
      setBusy(false);
      if (ok) {
        toast.success(msg || "Importado");
        if (diag && diag.filas_no_leidas > 0) {
          const top = (diag.por_cliente || []).slice(0, 6)
            .map((c) => `• ${c.cliente}: ${c.filas} fila(s) · US$ ${Number(c.saldo).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
            .join("\n");
          toast.warning(
            `⚠️ ${diag.filas_no_leidas} factura(s) no se pudieron leer (US$ ${Number(diag.saldo_no_leido).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}). Posible causa del descuadre con el consolidado.`,
            { description: top, duration: 60000, dismissible: true }
          );
        }
        onDone?.();
      } else { toast.error(msg || "No se pudo importar el PDF"); }
    };
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (vendedor.trim()) fd.append("vendedor", vendedor.trim());
      const res = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = res.data?.job_id;
      if (!jobId) { finish(true, res.data?.message); return; }
      const tid = toast.loading("Procesando el PDF… puede tardar varios minutos en documentos grandes.");
      const started = Date.now();
      const MAX_MS = 30 * 60 * 1000; // hasta 30 min: soporta PDFs muy largos
      let fails = 0; // fallos de red consecutivos tolerados antes de rendirse
      const poll = async () => {
        try {
          const st = await api.get(`/import-status/${jobId}`, { timeout: 30000 });
          fails = 0;
          const s = st.data?.status;
          if (s === "done") { toast.dismiss(tid); finish(true, st.data?.message, st.data?.diagnostico); return; }
          if (s === "error") { toast.dismiss(tid); finish(false, st.data?.message); return; }
          if (Date.now() - started > MAX_MS) {
            toast.dismiss(tid);
            finish(false, "La importación sigue procesándose en el servidor. Actualizá la lista en unos minutos para ver el resultado.");
            return;
          }
          setTimeout(poll, 3000);
        } catch (err) {
          // Un 404 real = el trabajo no existe: abortar de inmediato.
          if (err.response?.status === 404) {
            toast.dismiss(tid);
            finish(false, "No se encontró el trabajo de importación.");
            return;
          }
          // Fallos transitorios (502/520/timeout de red): reintentar, NO rendirse al primero.
          fails += 1;
          if (fails >= 10 || Date.now() - started > MAX_MS) {
            toast.dismiss(tid);
            finish(false, "Se perdió la conexión con el servidor. La importación puede seguir en curso; actualizá la lista en unos minutos.");
            return;
          }
          setTimeout(poll, 5000);
        }
      };
      poll();
    } catch (err) {
      finish(false, formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept=".pdf" className="hidden" onChange={handle} data-testid={`${testid}-file-input`} />
      {requireVendedor && (
        <>
          <input list={`${testid}-vend-list`} data-testid={`${testid}-vendedor`} value={vendedor}
            onChange={(e) => setVendedor(e.target.value)} placeholder="Vendedor del PDF"
            className="h-9 w-56 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <datalist id={`${testid}-vend-list`}>{vendedores.map((v) => <option key={v} value={v} />)}</datalist>
        </>
      )}
      <Button size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()}
        data-testid={`${testid}-import-btn`} className="gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {label}
      </Button>
    </div>
  );
}

function ProductoMultiSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (name) =>
    onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  const label =
    value.length === 0 ? "Todos los productos" :
    value.length === 1 ? value[0] :
    `${value.length} productos`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="cd-producto-filter"
          className="h-9 w-60 rounded-md border border-slate-200 px-3 text-sm flex items-center justify-between gap-1 bg-white hover:bg-slate-50 transition-colors"
        >
          <span className={`truncate ${value.length ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>{label}</span>
          <span className="flex items-center gap-1 shrink-0">
            {value.length > 0 && (
              <X className="h-3.5 w-3.5 text-slate-400 hover:text-red-500"
                 data-testid="cd-producto-clear"
                 onClick={(e) => { e.stopPropagation(); onChange([]); }} />
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar producto…" data-testid="cd-producto-search" />
          <CommandList>
            <CommandEmpty>Sin productos.</CommandEmpty>
            <CommandGroup>
              {options.map((name) => {
                const sel = value.includes(name);
                return (
                  <CommandItem key={name} value={name} onSelect={() => toggle(name)}
                    data-testid={`cd-producto-opt-${name}`} className="cursor-pointer">
                    <Check className={`mr-2 h-4 w-4 ${sel ? "opacity-100 text-emerald-600" : "opacity-0"}`} />
                    <span className="text-[12px]">{name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function CuentasDetalle({ fechaCorte, canDelete }) {
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ clientes: [], vendedores: [], coordinadores: [], tipos: [], productos: [] });
  const [total, setTotal] = useState(0);
  const [porTipo, setPorTipo] = useState({});
  const [topProductos, setTopProductos] = useState([]);
  const [prodTotales, setProdTotales] = useState(null);
  const [loading, setLoading] = useState(true);

  const [cliente, setCliente] = useState("");
  const [productos, setProductos] = useState([]);
  const [tipoVenta, setTipoVenta] = useState("__all__");
  const [vendedor, setVendedor] = useState("__all__");
  const [coordinador, setCoordinador] = useState("__all__");
  const [lastDiag, setLastDiag] = useState(null);
  const [showDiag, setShowDiag] = useState(false);
  const [estado, setEstado] = useState("__all__");
  const [showTable, setShowTable] = useState(false);

  const prodFilterActive = productos.length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { fecha_corte: fechaCorte };
      if (cliente.trim()) params.cliente = cliente.trim();
      if (productos.length) params.producto = productos;
      if (tipoVenta !== "__all__") params.tipo_venta = tipoVenta;
      if (vendedor !== "__all__") params.vendedor = vendedor;
      if (coordinador !== "__all__") params.coordinador = coordinador;
      if (estado !== "__all__") params.estado = estado;
      const res = await api.get("/cuentas-detalle", { params, paramsSerializer: { indexes: null } });
      setRows(res.data.items || []);
      setFacets(res.data.facets || { clientes: [], vendedores: [], coordinadores: [], tipos: [], productos: [] });
      setTotal(res.data.total || 0);
      setPorTipo(res.data.por_tipo || {});
      setTopProductos(res.data.top_productos || []);
      setProdTotales(res.data.prod_totales || null);
      try { const dg = await api.get("/cuentas-detalle/last-diag"); setLastDiag(dg.data?.diagnostico || null); } catch { /* noop */ }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar el detalle");
    } finally {
      setLoading(false);
    }
  }, [cliente, productos, tipoVenta, vendedor, coordinador, estado, fechaCorte]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportExcel = () => {
    if (!rows.length) { toast.error("No hay datos para exportar"); return; }
    const flat = [];
    rows.forEach((r) => {
      (r.productos || []).forEach((p) => {
        flat.push({
          Cliente: r.cliente, Vendedor: r.vendedor_nombre || r.vendedor, Pedido: r.pedido,
          "Vto. usado": r.vto_usado || r.fecha_vencimiento, "Venc. Orig.": r.venc_orig || "", "Tipo de Venta": r.tipo_venta,
          Producto: p.nombre, Cantidad: p.cantidad, Precio: p.precio,
          "Valor Original": p.importe_original ?? p.importe,
          "Nota de Crédito": p.credito || 0, "Saldo (USD)": p.importe,
          Estado: r.estado === "vencido" ? `Vencido (${r.dias_atraso}d)` : "A vencer",
        });
      });
    });
    if (prodFilterActive && prodTotales) {
      flat.push({
        Cliente: "TOTAL", Producto: `${productos.length} producto(s)`,
        Cantidad: prodTotales.cantidad, "Valor Original": prodTotales.original,
        "Nota de Crédito": prodTotales.credito, "Saldo (USD)": prodTotales.neto,
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(flat), "Detalle Cuentas");
    XLSX.writeFile(wb, `Detalle_Cuentas_Tecnomyl_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Excel descargado");
  };

  const exportHD = async () => {
    const tid = toast.loading("Generando imagen HD…");
    try {
      await exportImageHD("cuentas-detalle", { scale: 2.2 });
      toast.success("Imagen HD descargada", { id: tid });
    } catch {
      toast.error("No se pudo generar la imagen HD", { id: tid });
    }
  };

  const [deleting, setDeleting] = useState(false);
  const borrarTodo = async () => {
    if (!window.confirm("¿Borrar TODO lo cargado en esta sección (detalle de facturas y notas de crédito)? Esta acción no se puede deshacer.")) return;
    setDeleting(true);
    const tid = toast.loading("Borrando lo cargado…");
    try {
      const res = await api.delete("/cuentas-detalle");
      toast.success(res.data?.message || "Datos eliminados", { id: tid });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo borrar", { id: tid });
    } finally {
      setDeleting(false);
    }
  };

  const empty = !loading && rows.length === 0;

  return (
    <SectionCard
      id="cuentas-detalle"
      testid="section-cuentas-detalle"
      num="2.1"
      title="Detalle de Cuentas a Recibir por Producto y Tipo de Venta"
      description="Listado detallado por factura (Sal.Correg). Se excluyen las líneas canceladas. Vencimiento según Pagaré Vto."
      action={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportExcel} data-testid="cd-export-excel" className="gap-1.5">
            <Sheet className="h-4 w-4 text-emerald-600" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={exportHD} data-testid="cd-export-hd" className="gap-1.5">
            <ImageIcon className="h-4 w-4 text-emerald-700" /> Imagen HD
          </Button>
          <ImportBtn onDone={load} />
          <ImportBtn onDone={load} endpoint="/notas-credito/import" label="Cargar Notas de Crédito" testid="notas-credito" />
          {canDelete && (
            <Button size="sm" variant="outline" onClick={borrarTodo} disabled={deleting} data-testid="cd-delete-btn"
              className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Borrar cargado
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Buscar cliente…"
          className="h-9 w-48 rounded-md border border-slate-200 px-3 text-sm" data-testid="cd-cliente-filter" />
        <ProductoMultiSelect options={facets.productos} value={productos} onChange={setProductos} />
        <Select value={tipoVenta} onValueChange={setTipoVenta}>
          <SelectTrigger className="h-9 w-52" data-testid="cd-tipo-filter"><SelectValue placeholder="Tipo de venta" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los tipos</SelectItem>
            {facets.tipos.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={vendedor} onValueChange={setVendedor}>
          <SelectTrigger className="h-9 w-36" data-testid="cd-vendedor-filter"><SelectValue placeholder="Vendedor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos vend.</SelectItem>
            {facets.vendedores.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={coordinador} onValueChange={setCoordinador}>
          <SelectTrigger className="h-9 w-44" data-testid="cd-coordinador-filter"><SelectValue placeholder="Coordinador" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos coord.</SelectItem>
            {(facets.coordinadores || []).map((cc) => <SelectItem key={cc} value={cc}>{cc}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="h-9 w-40" data-testid="cd-estado-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            <SelectItem value="vencido">Vencido</SelectItem>
            <SelectItem value="a_vencer">A vencer</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500 ml-auto" data-testid="cd-count">{rows.length} factura(s)</span>
      </div>

      {lastDiag && lastDiag.filas_no_leidas > 0 && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid="cd-diag-panel">
          <button onClick={() => setShowDiag(!showDiag)}
            className="flex items-center gap-2 text-sm font-semibold text-amber-800" data-testid="cd-diag-toggle">
            <span>⚠️ {lastDiag.filas_no_leidas} factura(s) no se pudieron leer (US$ {Number(lastDiag.saldo_no_leido).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})</span>
            <span className="text-xs underline">{showDiag ? "ocultar" : "ver detalle"}</span>
          </button>
          {showDiag && (
            <div className="mt-2 space-y-2">
              <div className="text-xs text-amber-900">
                {(lastDiag.por_cliente || []).map((c, i) => (
                  <div key={i}>• {c.cliente}: {c.filas} fila(s) · US$ {Number(c.saldo).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                ))}
              </div>
              {(lastDiag.muestras || []).length > 0 && (
                <pre className="text-[10px] leading-4 bg-white border border-amber-200 rounded p-2 overflow-x-auto whitespace-pre-wrap"
                  data-testid="cd-diag-muestras">{(lastDiag.muestras || []).join("\n")}</pre>
              )}
            </div>
          )}
        </div>
      )}

      {productos.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="cd-selected-products">
          {productos.map((p) => (
            <span key={p} className="text-xs rounded-full bg-emerald-600 text-white px-2.5 py-1 flex items-center gap-1">
              {p}
              <X className="h-3 w-3 cursor-pointer hover:text-red-200"
                 onClick={() => setProductos(productos.filter((x) => x !== p))} />
            </span>
          ))}
        </div>
      )}

      {Object.keys(porTipo).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="cd-por-tipo">
          {Object.entries(porTipo).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <span key={k} className="text-xs rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1">
              {k}: <strong>{money(v)}</strong>
            </span>
          ))}
        </div>
      )}

      {topProductos.length > 0 && (
        <div className="mb-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid="cd-top-productos">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
            Top productos (por importe)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {topProductos.map((p, i) => (
              <span key={p.producto} className="text-xs rounded-full bg-white border border-slate-200 px-2.5 py-1 text-slate-700">
                <span className="text-emerald-700 font-semibold mr-1">#{i + 1}</span>
                {p.producto} <strong className="text-slate-900">· {money(p.importe)}</strong>
                <span className="text-slate-400"> ({p.facturas})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          onClick={() => setShowTable((s) => !s)}
          data-testid="cd-toggle-listado"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
          {showTable ? <ChevronUp className="h-4 w-4 text-emerald-700" /> : <ChevronDown className="h-4 w-4 text-emerald-700" />}
          {showTable ? "Ocultar listado detallado" : `Ver listado detallado (${rows.length} factura(s))`}
        </button>
        {!showTable && (
          <span className="text-xs text-slate-400">El detalle por factura está contraído para agilizar la carga.</span>
        )}
      </div>

      {showTable && (
      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
              <th className="text-left px-2.5 py-2.5 font-semibold min-w-[150px] border border-slate-100">Cliente</th>
              <th className="text-left px-2 py-2.5 font-semibold border border-slate-100">Vendedor</th>
              <th className="text-left px-2 py-2.5 font-semibold border border-slate-100">Pedido</th>
              <th className="text-left px-2 py-2.5 font-semibold whitespace-nowrap border border-slate-100">Vto. usado</th>
              <th className="text-left px-2 py-2.5 font-semibold border border-slate-100">Tipo de Venta</th>
              <th className="text-left px-2 py-2.5 font-semibold min-w-[220px] border border-slate-100">Productos</th>
              <th className="text-right px-2 py-2.5 font-semibold border border-slate-100">Cantidad</th>
              <th className="text-right px-2 py-2.5 font-semibold border border-slate-100">Precio</th>
              <th className="text-right px-2 py-2.5 font-semibold border border-slate-100">Total</th>
              <th className="text-right px-2 py-2.5 font-semibold whitespace-nowrap border border-slate-100">Nota de Crédito</th>
              <th className="text-right px-2 py-2.5 font-semibold whitespace-nowrap border border-slate-100">Saldo U$D</th>
              <th className="text-center px-2 py-2.5 font-semibold border border-slate-100">Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : empty ? (
              <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-400">Sin facturas. Cargá el PDF detallado de Cuentas a Recibir.</td></tr>
            ) : rows.map((r, i) => {
              const prods = r.productos || [];
              const lines = prods.map((p) => ({ type: "prod", p }));
              if (!prodFilterActive && (r.pago_devolucion || 0) > 0) lines.push({ type: "pago" });
              if (!prodFilterActive && (r.descuento_financiero || 0) > 0) lines.push({ type: "desc" });
              if (lines.length === 0) lines.push({ type: "empty" });
              const span = lines.length;
              const vencido = r.estado === "vencido";
              const rowBg = vencido ? "bg-red-50/40" : "";
              const bc = "border border-slate-100 align-top";
              return lines.map((ln, li) => (
                <tr key={`${i}-${li}`} className={`${rowBg} hover:bg-emerald-50/30`} data-testid={li === 0 ? `cd-row-${i}` : undefined}>
                  {li === 0 && (
                    <>
                      <td rowSpan={span} className={`${bc} px-2.5 py-2 font-medium text-slate-800 text-[12px]`}>{r.cliente}</td>
                      <td rowSpan={span} className={`${bc} px-2 py-2 text-slate-600 text-[12px]`}>{r.vendedor_nombre || r.vendedor || "—"}</td>
                      <td rowSpan={span} className={`${bc} px-2 py-2 font-mono text-[12px] text-slate-600`}>{r.pedido}</td>
                      <td rowSpan={span} className={`${bc} px-2 py-2 whitespace-nowrap text-[12px] text-slate-700`} data-testid={`cd-vto-${i}`}>
                        <div className="font-medium">{r.vto_usado || r.fecha_vencimiento || "—"}</div>
                        {r.venc_orig && r.venc_orig !== (r.vto_usado || r.fecha_vencimiento) && (
                          <div className="text-[10px] text-slate-400">orig.: {r.venc_orig}</div>
                        )}
                      </td>
                      <td rowSpan={span} className={`${bc} px-2 py-2 text-[12px] text-slate-700`}>{r.tipo_venta}</td>
                    </>
                  )}
                  {ln.type === "prod" && (
                    <>
                      <td className={`${bc} px-2 py-1.5 text-[12px] text-slate-700`} data-testid={`cd-prod-${i}-${li}`}>{ln.p.nombre}</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums text-slate-600`}>{nfmt(ln.p.cantidad)}</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums text-slate-600`}>{money(ln.p.precio)}</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums text-slate-700`}>{money(ln.p.importe_original ?? ln.p.importe)}</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums ${ln.p.credito > 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>{ln.p.credito > 0 ? money(ln.p.credito) : "-"}</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums font-semibold text-slate-900`}>{money(ln.p.importe)}</td>
                    </>
                  )}
                  {ln.type === "pago" && (
                    <>
                      <td className={`${bc} px-2 py-1.5 text-[12px] italic text-sky-700`} data-testid={`cd-pago-${i}`}>Pago / Devolución</td>
                      <td className={`${bc}`} /><td className={`${bc}`} />
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums text-slate-400`}>-</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums text-slate-400`}>-</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums font-semibold text-sky-700`}>− {money(r.pago_devolucion)}</td>
                    </>
                  )}
                  {ln.type === "desc" && (
                    <>
                      <td className={`${bc} px-2 py-1.5 text-[12px] italic text-amber-700`} data-testid={`cd-descfin-${i}`}>Descuento financiero</td>
                      <td className={`${bc}`} /><td className={`${bc}`} />
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums text-slate-400`}>-</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums text-red-600 font-semibold`}>{money(r.descuento_financiero)}</td>
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums font-semibold text-amber-700`}>− {money(r.descuento_financiero)}</td>
                    </>
                  )}
                  {ln.type === "empty" && (
                    <>
                      <td className={`${bc} px-2 py-1.5 text-[12px] text-slate-400`}>—</td>
                      <td className={`${bc}`} /><td className={`${bc}`} /><td className={`${bc}`} /><td className={`${bc}`} />
                      <td className={`${bc} px-2 py-1.5 text-right text-[12px] tabular-nums font-semibold text-slate-900`}>{money(r.sal_correg)}</td>
                    </>
                  )}
                  {li === 0 && (
                    <td rowSpan={span} className={`${bc} px-2 py-2 text-center text-[12px] whitespace-nowrap`}>
                      {vencido
                        ? <span className="text-red-600 font-semibold">🔴 {r.dias_atraso}d</span>
                        : <span className="text-emerald-700">🟢 A vencer</span>}
                    </td>
                  )}
                </tr>
              ));
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              {prodFilterActive && prodTotales ? (
                <tr className="bg-emerald-50 border-t-2 border-emerald-200 font-semibold text-[13px]" data-testid="cd-total-producto">
                  <td className="px-3 py-2.5 text-emerald-900 uppercase text-[11px] tracking-wide border border-emerald-100" colSpan={6}>
                    Total producto{productos.length > 1 ? "s" : ""} ({rows.length} facturas)
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-emerald-900 tabular-nums border border-emerald-100" data-testid="cd-total-cantidad">{nfmt(prodTotales.cantidad)}</td>
                  <td className="border border-emerald-100" />
                  <td className="px-2 py-2.5 text-right font-mono text-emerald-900 tabular-nums border border-emerald-100" data-testid="cd-total-original">{money(prodTotales.original)}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-red-600 tabular-nums border border-emerald-100" data-testid="cd-total-credito">{prodTotales.credito > 0 ? money(prodTotales.credito) : "-"}</td>
                  <td className="px-2 py-2.5 text-right font-mono text-emerald-900 tabular-nums border border-emerald-100" data-testid="cd-total-neto">{money(prodTotales.neto)}</td>
                  <td className="border border-emerald-100" />
                </tr>
              ) : (
                <tr className="bg-emerald-50 border-t-2 border-emerald-200 font-semibold text-[13px]">
                  <td className="px-3 py-2.5 text-emerald-900 uppercase text-[11px] tracking-wide border border-emerald-100" colSpan={10}>Total ({rows.length} facturas)</td>
                  <td className="px-2 py-2.5 text-right font-mono text-emerald-900 tabular-nums border border-emerald-100">{money(total)}</td>
                  <td className="border border-emerald-100" />
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
      )}
    </SectionCard>
  );
}
